import '../styles/night-sky.css';

/**
 * Realistic Night Sky Engine
 * Astronomically accurate star rendering with real catalog data.
 * Two canvas layers: static (real stars + Milky Way) and animated (twinkling + parallax).
 *
 * Replaces the old starfield.ts with stereographic projection from ~40N latitude,
 * local sidereal time ~20h (summer evening looking south).
 */

import catalog from './star-catalog.json';
import { onRoomChange } from '../router.js';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CatalogStar {
  ra: number;        // right ascension in hours
  dec: number;       // declination in degrees
  mag: number;       // apparent magnitude
  bv: number;        // B-V color index
  name?: string;     // common name
  // Projected screen coords (filled after projection)
  sx: number;
  sy: number;
  altitude: number;  // degrees above horizon
}

interface TwinklingStar {
  catalogIdx: number;
  speed1: number;
  speed2: number;
  offset1: number;
  offset2: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DEG = Math.PI / 180;
const OBSERVER_LAT = 43.65 * DEG;        // Toronto ~40N
const LOCAL_SIDEREAL_TIME = 20;           // hours, summer evening
const SIN_LAT = Math.sin(OBSERVER_LAT);
const COS_LAT = Math.cos(OBSERVER_LAT);

const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches;

// B-V color mapping
const BV_COLORS: [number, string][] = [
  [-Infinity, '#aaccff'],   // B-V < 0.0: blue-white
  [0.0,       '#eeeeff'],   // 0.0–0.4: white
  [0.4,       '#fff4e0'],   // 0.4–0.8: yellow-white
  [0.8,       '#ffd88a'],   // 0.8–1.2: warm yellow
  [1.2,       '#ffaa6b'],   // > 1.2: orange-red
];

const ATMOSPHERIC_RED = '#ffaa6b';

// ─── Module state ───────────────────────────────────────────────────────────

let animFrame: number | null = null;
let staticCanvas: HTMLCanvasElement | null = null;
let animCanvas: HTMLCanvasElement | null = null;
let containerRef: HTMLElement | null = null;

let cssWidth = 0;
let cssHeight = 0;
let dpr = 1;

// Parsed & projected star data
let projectedStars: CatalogStar[] = [];
let twinklingStars: TwinklingStar[] = [];

// Parallax state
let parallaxX = 0;
let parallaxY = 0;
let targetParallaxX = 0;
let targetParallaxY = 0;

// Star label state
let starLabelActive = false;
let starLabelTimer: number | null = null;
let currentLabelIdx = 0;
let toggleBtn: HTMLButtonElement | null = null;
let tooltipEl: HTMLDivElement | null = null;
let labelEl: HTMLDivElement | null = null;
let toggleDelayTimer: number | null = null;
let tooltipTimer: number | null = null;
let currentStarSx = 0;
let currentStarSy = 0;

// Handler references for cleanup
let resizeHandler: (() => void) | null = null;
let mouseMoveHandler: ((e: MouseEvent) => void) | null = null;
let visibilityHandler: (() => void) | null = null;
let lastTime = 0;

// Parallax subscribers — called each animation frame with current parallax
type ParallaxListener = (x: number, y: number) => void;
const _parallaxListeners: Set<ParallaxListener> = new Set();

export function subscribeParallax(cb: ParallaxListener): () => void {
  _parallaxListeners.add(cb);
  return () => _parallaxListeners.delete(cb);
}

// ─── Coordinate transforms ─────────────────────────────────────────────────

/**
 * Convert RA/Dec to altitude/azimuth for our observer.
 * RA in hours, Dec in degrees. Returns { alt, az } in degrees.
 */
function equatorialToHorizontal(raHours: number, decDeg: number): { alt: number; az: number } {
  const ha = (LOCAL_SIDEREAL_TIME - raHours) * 15 * DEG; // hour angle in radians
  const dec = decDeg * DEG;
  const sinDec = Math.sin(dec);
  const cosDec = Math.cos(dec);
  const cosHa = Math.cos(ha);
  const sinHa = Math.sin(ha);

  // Altitude
  const sinAlt = sinDec * SIN_LAT + cosDec * COS_LAT * cosHa;
  const alt = Math.asin(Math.max(-1, Math.min(1, sinAlt)));

  // Azimuth (measured from south, clockwise = west)
  const cosAlt = Math.cos(alt);
  let az: number;
  if (cosAlt < 1e-10) {
    az = 0;
  } else {
    const cosAz = (sinDec - Math.sin(alt) * SIN_LAT) / (cosAlt * COS_LAT);
    az = Math.acos(Math.max(-1, Math.min(1, cosAz)));
    if (sinHa > 0) az = 2 * Math.PI - az; // west side
  }

  return { alt: alt / DEG, az: az / DEG };
}

/**
 * Stereographic projection: altitude/azimuth to screen x/y.
 * South = center, east = left, west = right. Zenith = top, horizon = bottom.
 */
function horizontalToScreen(altDeg: number, azDeg: number): { x: number; y: number } {
  // Zenith distance
  const zd = (90 - altDeg) * DEG;
  // Stereographic radius from center (zenith)
  const r = 2 * Math.tan(zd / 2);

  // Azimuth: 0 = south, 90 = west, 270 = east
  // Map so south is center-bottom, east is left, west is right
  const azRad = azDeg * DEG;

  // x: sin(az) maps south(0) -> 0, west(90) -> +1, east(270) -> -1
  const px = r * Math.sin(azRad);
  // y: -cos(az) so south(0) goes down, north goes up
  // But we want zenith (alt=90) at top. The zenith is r=0 at center.
  // Stars near horizon have large r. We invert so zenith is top.
  const py = r * Math.cos(azRad);

  // Normalize to screen coordinates
  // The visible hemisphere has zd from 0 to 90 deg, r from 0 to 2.
  // We use a fraction of the canvas to keep a nice framing.
  const scale = Math.min(cssWidth, cssHeight) * 0.42;
  const cx = cssWidth / 2;
  const cy = cssHeight * 0.42; // zenith slightly above center

  return {
    x: cx + px * scale,
    y: cy + py * scale,
  };
}

// ─── Star color & rendering helpers ─────────────────────────────────────────

function bvToColor(bv: number): string {
  if (bv < 0.0)  return BV_COLORS[0][1];
  if (bv < 0.4)  return BV_COLORS[1][1];
  if (bv < 0.8)  return BV_COLORS[2][1];
  if (bv < 1.2)  return BV_COLORS[3][1];
  return BV_COLORS[4][1];
}

function starSize(mag: number): number {
  // Tight range matching dust-particle feel (0.4–1.6px)
  return Math.max(0.4, (5.5 - mag) * 0.35);
}

function starOpacity(mag: number): number {
  return Math.max(0.1, (5.5 - mag) / 5.5);
}

/**
 * Blend two hex colors. amt 0 = pure c1, amt 1 = pure c2.
 */
function blendHex(c1: string, c2: string, amt: number): string {
  const r1 = parseInt(c1.slice(1, 3), 16);
  const g1 = parseInt(c1.slice(3, 5), 16);
  const b1 = parseInt(c1.slice(5, 7), 16);
  const r2 = parseInt(c2.slice(1, 3), 16);
  const g2 = parseInt(c2.slice(3, 5), 16);
  const b2 = parseInt(c2.slice(5, 7), 16);
  const r = Math.round(r1 + (r2 - r1) * amt);
  const g = Math.round(g1 + (g2 - g1) * amt);
  const b = Math.round(b1 + (b2 - b1) * amt);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// ─── Star catalog parsing & projection ──────────────────────────────────────

function projectStars(): void {
  projectedStars = [];
  twinklingStars = [];

  const rawStars = catalog.stars as [number, number, number, number, string?][];

  for (let i = 0; i < rawStars.length; i++) {
    const [ra, dec, mag, bv, name] = rawStars[i];
    const { alt, az } = equatorialToHorizontal(ra, dec);

    // Skip stars below the horizon
    if (alt < 0) continue;

    const { x, y } = horizontalToScreen(alt, az);

    // Skip stars that fall far outside the viewport
    if (x < -50 || x > cssWidth + 50 || y < -50 || y > cssHeight + 50) continue;

    const idx = projectedStars.length;
    projectedStars.push({
      ra, dec, mag, bv,
      name: name || undefined,
      sx: x,
      sy: y,
      altitude: alt,
    });

    // Collect twinkling stars: mag <= 4.0, up to ~80
    if (mag <= 4.0 && twinklingStars.length < 80) {
      twinklingStars.push({
        catalogIdx: idx,
        speed1: 1.5 + Math.random() * 3.0,
        speed2: 0.7 + Math.random() * 2.0,
        offset1: Math.random() * Math.PI * 2,
        offset2: Math.random() * Math.PI * 2,
      });
    }
  }
}

function getNamedStars(): CatalogStar[] {
  return projectedStars
    .filter(s => s.name)
    .sort((a, b) => a.mag - b.mag);
}

// ─── Static layer rendering ─────────────────────────────────────────────────


function renderStarsToCanvas(ctx: CanvasRenderingContext2D): void {
  ctx.save();
  for (const star of projectedStars) {
    let color = bvToColor(star.bv);
    let opacity = starOpacity(star.mag);
    const size = starSize(star.mag);

    // Atmospheric extinction near horizon
    if (star.altitude < 10) {
      const factor = star.altitude / 10;
      opacity *= factor;
      color = blendHex(color, ATMOSPHERIC_RED, 0.2);
    }

    // Simple filled dot — clean, minimal, like dust particles
    ctx.globalAlpha = opacity;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(star.sx, star.sy, size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function renderStaticLayer(): void {
  if (!staticCanvas) return;

  // Use an offscreen canvas for rendering, then draw to the visible static canvas
  const offscreen = document.createElement('canvas');
  offscreen.width = cssWidth * dpr;
  offscreen.height = cssHeight * dpr;
  const offCtx = offscreen.getContext('2d');
  if (!offCtx) return;

  offCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // Transparent background — page dark color shows through
  // Just render star dots
  renderStarsToCanvas(offCtx);

  // Composite offscreen result onto the static canvas
  const staticCtx = staticCanvas.getContext('2d');
  if (!staticCtx) return;
  staticCtx.clearRect(0, 0, staticCanvas.width, staticCanvas.height);
  staticCtx.drawImage(offscreen, 0, 0);
}

// ─── Animated layer ─────────────────────────────────────────────────────────

function drawTwinklingFrame(time: number): void {
  if (!animCanvas) return;
  const ctx = animCanvas.getContext('2d');
  if (!ctx) return;

  ctx.clearRect(0, 0, animCanvas.width, animCanvas.height);
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const t = time * 0.001; // seconds

  for (const tw of twinklingStars) {
    const star = projectedStars[tw.catalogIdx];
    if (!star) continue;

    let color = bvToColor(star.bv);
    let baseOpacity = starOpacity(star.mag);
    const size = starSize(star.mag);

    // Atmospheric extinction
    if (star.altitude < 10) {
      const factor = star.altitude / 10;
      baseOpacity *= factor;
      color = blendHex(color, ATMOSPHERIC_RED, 0.2);
    }

    // Gentle sine-wave oscillation — same feel as dust particles
    const oscillation = 0.5 + 0.5 * Math.sin(t * tw.speed1 + tw.offset1);
    const opacity = baseOpacity * oscillation;

    // Simple filled dot
    ctx.globalAlpha = opacity;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(star.sx, star.sy, size, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

// ─── Animation loop ─────────────────────────────────────────────────────────

function animate(time: number): void {
  animFrame = requestAnimationFrame(animate);
  lastTime = time;

  // Smooth parallax interpolation
  parallaxX += (targetParallaxX - parallaxX) * 0.05;
  parallaxY += (targetParallaxY - parallaxY) * 0.05;

  // Mobile auto-drift
  if (isMobile) {
    targetParallaxX = Math.sin(time * 0.0002) * 8;
    targetParallaxY = Math.cos(time * 0.00015) * 5;
  }

  // Apply parallax as CSS transform on the static canvas
  const tx = Math.round(parallaxX * 10) / 10;
  const ty = Math.round(parallaxY * 10) / 10;
  if (staticCanvas) {
    staticCanvas.style.transform = `translate(${tx}px, ${ty}px)`;
  }
  if (animCanvas) {
    animCanvas.style.transform = `translate(${tx}px, ${ty}px)`;
  }
  // Notify parallax subscribers
  for (const cb of _parallaxListeners) {
    cb(parallaxX, parallaxY);
  }

  // Track star label with parallax
  if (starLabelActive && labelEl && labelEl.classList.contains('star-label--visible')) {
    labelEl.style.left = `${currentStarSx + parallaxX + 8}px`;
    labelEl.style.top = `${currentStarSy + parallaxY - 12}px`;
  }

  // Render twinkling stars
  drawTwinklingFrame(time);
}

// ─── Canvas creation & sizing ───────────────────────────────────────────────

function createCanvases(container: HTMLElement): void {
  dpr = window.devicePixelRatio || 1;
  cssWidth = window.innerWidth;
  cssHeight = window.innerHeight;

  // Static canvas (rendered once)
  staticCanvas = document.createElement('canvas');
  staticCanvas.className = 'night-sky-static';
  staticCanvas.width = cssWidth * dpr;
  staticCanvas.height = cssHeight * dpr;
  staticCanvas.style.cssText = `
    position: fixed;
    inset: 0;
    width: ${cssWidth}px;
    height: ${cssHeight}px;
    pointer-events: none;
    z-index: 0;
    will-change: transform;
  `;

  // Animated canvas (twinkling layer)
  animCanvas = document.createElement('canvas');
  animCanvas.className = 'night-sky-anim';
  animCanvas.width = cssWidth * dpr;
  animCanvas.height = cssHeight * dpr;
  animCanvas.style.cssText = `
    position: fixed;
    inset: 0;
    width: ${cssWidth}px;
    height: ${cssHeight}px;
    pointer-events: none;
    z-index: 1;
    will-change: transform;
  `;

  container.prepend(animCanvas);
  container.prepend(staticCanvas);
}

function resizeCanvases(): void {
  if (!containerRef) return;
  const newW = window.innerWidth;
  const newH = window.innerHeight;
  if (newW === cssWidth && newH === cssHeight) return;

  cssWidth = newW;
  cssHeight = newH;
  dpr = window.devicePixelRatio || 1;

  if (staticCanvas) {
    staticCanvas.width = cssWidth * dpr;
    staticCanvas.height = cssHeight * dpr;
    staticCanvas.style.width = `${cssWidth}px`;
    staticCanvas.style.height = `${cssHeight}px`;
  }
  if (animCanvas) {
    animCanvas.width = cssWidth * dpr;
    animCanvas.height = cssHeight * dpr;
    animCanvas.style.width = `${cssWidth}px`;
    animCanvas.style.height = `${cssHeight}px`;
  }

  // Re-project all stars for new dimensions
  projectStars();
  renderStaticLayer();
}

// ─── Event setup ────────────────────────────────────────────────────────────

function setupParallax(): void {
  if (isMobile) return; // mobile uses auto-drift

  mouseMoveHandler = (e: MouseEvent) => {
    targetParallaxX = ((e.clientX / cssWidth) - 0.5) * 30;   // ±15px
    targetParallaxY = ((e.clientY / cssHeight) - 0.5) * 30;
  };
  document.addEventListener('mousemove', mouseMoveHandler);
}

function setupVisibilityPause(): void {
  visibilityHandler = () => {
    if (document.hidden) {
      if (animFrame) { cancelAnimationFrame(animFrame); animFrame = null; }
    } else {
      lastTime = 0;
      if (!animFrame) animFrame = requestAnimationFrame(animate);
    }
  };
  document.addEventListener('visibilitychange', visibilityHandler);
}

// ─── Star label UI ──────────────────────────────────────────────────────────

function hideTooltip(): void {
  if (tooltipEl) tooltipEl.classList.remove('star-tooltip--visible');
  if (tooltipTimer) { clearTimeout(tooltipTimer); tooltipTimer = null; }
}

function showTooltip(): void {
  if (!tooltipEl) return;
  tooltipEl.classList.add('star-tooltip--visible');
  tooltipTimer = window.setTimeout(() => hideTooltip(), 5000);
}

function hideLabel(): void {
  if (labelEl) labelEl.classList.remove('star-label--visible');
}

function stopStarCycling(): void {
  if (starLabelTimer) { clearTimeout(starLabelTimer); starLabelTimer = null; }
}

function showNextStar(): void {
  if (!starLabelActive || !labelEl) return;
  const named = getNamedStars();
  if (named.length === 0) return;

  const star = named[currentLabelIdx % named.length];
  currentStarSx = star.sx;
  currentStarSy = star.sy;
  labelEl.style.left = `${currentStarSx + parallaxX + 8}px`;
  labelEl.style.top = `${currentStarSy + parallaxY - 12}px`;
  const color = bvToColor(star.bv);
  labelEl.style.color = color;
  labelEl.style.background = color + '11';
  labelEl.style.border = `1px solid ${color}22`;
  labelEl.textContent = star.name!;
  labelEl.classList.add('star-label--visible');

  starLabelTimer = window.setTimeout(() => {
    hideLabel();
    starLabelTimer = window.setTimeout(() => {
      currentLabelIdx++;
      showNextStar();
    }, 800);
  }, 3000);
}

function startStarCycling(): void {
  currentLabelIdx = 0;
  showNextStar();
}

function handleStarToggle(): void {
  starLabelActive = !starLabelActive;
  if (starLabelActive) {
    document.body.classList.add('stars-revealed');
    if (toggleBtn) toggleBtn.classList.add('star-toggle--active');
    showTooltip();
    startStarCycling();
  } else {
    document.body.classList.remove('stars-revealed');
    if (toggleBtn) toggleBtn.classList.remove('star-toggle--active');
    hideTooltip();
    stopStarCycling();
    hideLabel();
  }
}

function createStarLabelUI(container: HTMLElement): void {
  toggleBtn = document.createElement('button');
  toggleBtn.className = 'star-toggle';
  toggleBtn.textContent = 'do you look to the stars?';
  toggleBtn.addEventListener('click', handleStarToggle);
  container.appendChild(toggleBtn);

  tooltipEl = document.createElement('div');
  tooltipEl.className = 'star-tooltip';
  tooltipEl.textContent = 'me too. these are 241 real stars from the Yale Bright Star Catalog, projected from Toronto on a summer night.';
  container.appendChild(tooltipEl);

  labelEl = document.createElement('div');
  labelEl.className = 'star-label';
  container.appendChild(labelEl);

  toggleDelayTimer = window.setTimeout(() => {
    if (toggleBtn) toggleBtn.classList.add('star-toggle--visible');
  }, 3000);
}

function destroyStarLabelUI(): void {
  document.body.classList.remove('stars-revealed');
  stopStarCycling();
  hideTooltip();
  if (toggleDelayTimer) { clearTimeout(toggleDelayTimer); toggleDelayTimer = null; }
  if (toggleBtn) { toggleBtn.remove(); toggleBtn = null; }
  if (tooltipEl) { tooltipEl.remove(); tooltipEl = null; }
  if (labelEl) { labelEl.remove(); labelEl = null; }
  starLabelActive = false;
  currentLabelIdx = 0;
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Initialize the night sky with two canvas layers inside the given container.
 * Renders the static layer once (stars + Milky Way), then starts
 * the animation loop for twinkling and parallax.
 */
export function initNightSky(container: HTMLElement): void {
  containerRef = container;

  createCanvases(container);
  projectStars();
  renderStaticLayer();

  setupParallax();
  setupVisibilityPause();

  resizeHandler = () => resizeCanvases();
  window.addEventListener('resize', resizeHandler);

  animFrame = requestAnimationFrame(animate);
  createStarLabelUI(container);

  // Only show the star toggle on the lobby — it's contextually irrelevant elsewhere
  // and causes overlap issues on other pages (especially mobile).
  onRoomChange((room) => {
    if (!toggleBtn) return;
    const isLobby = room === 'lobby';
    toggleBtn.style.display = isLobby ? '' : 'none';

    // Deactivate star labels when leaving lobby
    if (!isLobby && starLabelActive) {
      starLabelActive = false;
      document.body.classList.remove('stars-revealed');
      toggleBtn.classList.remove('star-toggle--active');
      hideTooltip();
      stopStarCycling();
      hideLabel();
    }
  });
}

/**
 * Tear down: cancel animation, remove canvases, clean up all listeners, null refs.
 */
export function destroyNightSky(): void {
  destroyStarLabelUI();

  // Cancel animation
  if (animFrame) { cancelAnimationFrame(animFrame); animFrame = null; }

  // Remove event listeners
  if (resizeHandler) { window.removeEventListener('resize', resizeHandler); resizeHandler = null; }
  if (mouseMoveHandler) { document.removeEventListener('mousemove', mouseMoveHandler); mouseMoveHandler = null; }
  if (visibilityHandler) { document.removeEventListener('visibilitychange', visibilityHandler); visibilityHandler = null; }

  // Remove canvases
  if (staticCanvas) { staticCanvas.remove(); staticCanvas = null; }
  if (animCanvas) { animCanvas.remove(); animCanvas = null; }

  // Reset state
  containerRef = null;
  projectedStars = [];
  twinklingStars = [];
  parallaxX = 0;
  parallaxY = 0;
  targetParallaxX = 0;
  targetParallaxY = 0;
  lastTime = 0;
  cssWidth = 0;
  cssHeight = 0;
}

/**
 * Returns the animated canvas element for overlay positioning.
 */
export function getNightSkyCanvas(): HTMLCanvasElement | null {
  return animCanvas;
}

/**
 * Read-only access to projected star catalog (available after initNightSky).
 */
export function getProjectedStars(): ReadonlyArray<Readonly<CatalogStar>> {
  return projectedStars;
}
